import * as React from 'react';
import { SearchResponse } from '@algolia/client-search';

interface State<T> {
  loading: boolean;
  hasError: boolean;
  data: SearchResponse<T> | null;
}

type Action<T> =
  | { type: 'LOADING_STARTED' }
  | { type: 'LOADING_COMPLETED'; payload: SearchResponse<T> | null }
  | { type: 'LOADING_ERRORED' }
  | { type: 'LOADING_SKIPPED' };

const initialState = {
  data: null,
  loading: true,
  hasError: false,
};

function reducer<T>(state: State<T>, action: Action<T>): State<T> {
  switch (action.type) {
    case 'LOADING_STARTED':
      return { ...state, loading: true, hasError: false };
    case 'LOADING_COMPLETED':
      return {
        ...state,
        loading: false,
        data: action.payload,
        hasError: false,
      };
    case 'LOADING_ERRORED':
      return { ...state, loading: false, data: null, hasError: true };
    case 'LOADING_SKIPPED':
      return { ...state, loading: false, data: null, hasError: false };
    default:
      return state;
  }
}

export const useAlgoliaResponse = function <T, U>(
  search: (p: U) => Promise<SearchResponse<T> | null>,
  params: any,
  bail = false,
): [any, boolean, SearchResponse<T> | null] {
  const [version, setVersion] = React.useState(0);
  const responseRef = React.useRef(0);

  const [{ data, loading, hasError }, dispatch] = React.useReducer(reducer, initialState);

  const searchRef = React.useRef(search);
  const paramsRef = React.useRef(JSON.stringify(params));

  React.useEffect(() => {
    if (!bail && paramsRef.current !== JSON.stringify(params)) {
      paramsRef.current = JSON.stringify(params);
      setVersion((version) => version + 1);
    }
  }, [bail, params]);

  React.useEffect(() => {
    if (!bail) {
      dispatch({ type: 'LOADING_STARTED' });
      searchRef
        .current(JSON.parse(paramsRef.current))
        .then((payload) => {
          dispatch({
            type: 'LOADING_COMPLETED',
            payload,
          });
          responseRef.current = responseRef.current + 1;
        })
        .catch(() => {
          dispatch({ type: 'LOADING_ERRORED' });
        });
    } else {
      dispatch({
        type: 'LOADING_SKIPPED',
      });
    }
  }, [bail, version]);

  return [hasError, loading, data as SearchResponse<T> | null];
};
